import type { ListColumnType } from './contract.ts';
import {
  COMPARE_COLUMN,
  LIST_QUERY_V2,
  LIST_QUERY_VERSION,
  listFilterOperators,
  upgradeListQuery,
  type AnyListQuery,
  type ListCondition,
  type ListFilter,
  type ListFilterGroup,
  type ListQueryV2,
} from './filter.ts';

/**
 * Filter im Spaltenkopf (Excel-Autofilter-Stil, Story 1.32 Oberfläche) und Helfer für den gemeinsamen Filterzustand
 * (Filter-Beschreibung Version 2) von Filterleiste und Spaltenkopf. Rein – auch die Web-App nutzt es.
 */

/**
 * Art des Spaltenkopf-Filters:
 * - `values`: Werteliste mit Suchfeld und Mehrfachauswahl (Auswahl, Status, Ja/Nein und Text-Spalten mit überschaubar
 *   vielen Werten – Faustregel ≤ ~200 verschiedene, angemeldet über `distinctOptions` mit Index des Besitzers);
 * - `contains`: freier Text („enthält“), z. B. Name, Artikelnummer, E-Mail;
 * - `range`: Zahl, Betrag, Menge, Prozent von–bis;
 * - `date`: relative Zeiträume („letzte 30 Tage“, „vor mehr als … Tagen“) bzw. von–bis.
 * `null` = nicht filterbar (kein Symbol im Kopf).
 */
export type HeaderFilterKind = 'values' | 'contains' | 'range' | 'date';

export function headerFilterKind(column: { type: ListColumnType; filterable: boolean; distinctOptions?: boolean | undefined }): HeaderFilterKind | null {
  if (!column.filterable) return null;
  switch (column.type) {
    case 'text':
      return column.distinctOptions ? 'values' : 'contains';
    case 'enum':
    case 'status':
    case 'multi_enum':
    case 'boolean':
      return 'values';
    case 'number':
    case 'money':
    case 'quantity':
    case 'percent':
      return 'range';
    case 'date':
    case 'datetime':
      return 'date';
    default:
      return null;
  }
}

const V1_OPERATORS: ReadonlySet<string> = new Set(listFilterOperators);

/**
 * Kleinste gleichwertige Beschreibung: Version 2 ohne Gruppen, ohne „nicht“, nur mit Operatoren der Version 1, mit
 * Wurzel „und“ und ohne Schnellsuche wird Version 1 (gespeicherte Ansichten und „alle dieser Ansicht bearbeiten“
 * bleiben bei einfachen Filtern unverändert). Sonst unverändert; leere Suche fällt weg.
 */
export function compactListQuery(query: AnyListQuery): AnyListQuery {
  if (query.version === LIST_QUERY_VERSION) return query;
  const search = query.search?.trim() ?? '';
  const { where } = query;
  const flat =
    search === '' &&
    (where.op === 'and' || where.items.length <= 1) &&
    where.items.every((item) => item.type === 'condition' && !item.not && V1_OPERATORS.has(item.op));
  if (flat) {
    return {
      version: LIST_QUERY_VERSION,
      filters: where.items.map((item) => {
        const c = item as ListCondition;
        return (c.value === undefined ? { column: c.column, op: c.op } : { column: c.column, op: c.op, value: c.value }) as ListFilter;
      }),
      sort: query.sort,
    };
  }
  const { search: _omit, ...rest } = query;
  void _omit;
  return search === '' ? rest : { ...rest, search };
}

/** Beschreibung mit (anderer) Schnellsuche; leer = keine Suche. Ergebnis immer Version 2 (Suche gibt es nur dort). */
export function withSearch(query: AnyListQuery, search: string): ListQueryV2 {
  const v2 = upgradeListQuery(query);
  const { search: _old, ...rest } = v2;
  void _old;
  const term = search.trim();
  return term === '' ? rest : { ...rest, search: term };
}

/** Zahl der Bedingungen (alle Ebenen). */
export function countConditions(group: ListFilterGroup): number {
  return group.items.reduce((n, item) => n + (item.type === 'group' ? countConditions(item) : 1), 0);
}

/** Spalten, die eine Bedingung nennt (auch Vergleichsspalten), über alle Ebenen. */
export function filteredColumnKeys(group: ListFilterGroup): Set<string> {
  const out = new Set<string>();
  const walk = (g: ListFilterGroup) => {
    for (const item of g.items) {
      if (item.type === 'group') walk(item);
      else if (item.type === 'condition') {
        out.add(item.column);
        if (item.op === COMPARE_COLUMN && typeof item.value === 'object' && item.value !== null && 'column' in item.value) out.add(String(item.value.column));
      }
    }
  };
  walk(group);
  return out;
}

/** Bedingung des Spaltenkopfs: die erste Bedingung der Wurzel auf dieser Spalte ohne „nicht“ (oder `null`). */
export function headerCondition(query: AnyListQuery, column: string): ListCondition | null {
  const found = upgradeListQuery(query).where.items.find((item) => item.type === 'condition' && item.column === column && !item.not);
  return (found as ListCondition | undefined) ?? null;
}

/**
 * Spaltenkopf-Filter setzen: ersetzt die Bedingung des Spaltenkopfs (siehe `headerCondition`) bzw. hängt sie an die
 * Wurzel an; `null` entfernt alle Bedingungen, die die Spalte nennen (auch als Vergleichsspalte), auf allen Ebenen
 * („Filter zurücksetzen“). Leere
 * Untergruppen bleiben stehen (sie schränken nicht ein).
 */
export function setHeaderCondition(query: AnyListQuery, column: string, condition: ListCondition | null): ListQueryV2 {
  const v2 = upgradeListQuery(query);
  if (condition === null) return { ...v2, where: removeColumn(v2.where, column) };
  const items = [...v2.where.items];
  const at = items.findIndex((item) => item.type === 'condition' && item.column === column && !item.not);
  if (at >= 0) items[at] = condition;
  else items.push(condition);
  return { ...v2, where: { ...v2.where, items } };
}

/** Nennt die Bedingung die Spalte (selbst oder als Vergleichsspalte von `compare_column`)? */
function mentions(item: ListCondition, column: string): boolean {
  if (item.column === column) return true;
  return item.op === COMPARE_COLUMN && typeof item.value === 'object' && item.value !== null && 'column' in item.value && item.value.column === column;
}

function removeColumn(group: ListFilterGroup, column: string): ListFilterGroup {
  const items: ListFilterGroup['items'] = [];
  for (const item of group.items) {
    if (item.type === 'group') items.push(removeColumn(item, column));
    else if (item.type === 'relation' || !mentions(item, column)) items.push(item);
  }
  return { ...group, items };
}

/**
 * Wird die Spalte außerhalb des Spaltenkopfs gefiltert (in einer Gruppe, mit „nicht“ oder als Vergleichsspalte)? Dann
 * zeigt der Spaltenkopf einen Hinweis; „Filter zurücksetzen“ entfernt auch diese Bedingungen.
 */
export function filteredElsewhere(query: AnyListQuery, column: string): boolean {
  // Einmal hochstufen: Version 1 erzeugt bei jedem Hochstufen neue Objekte; verglichen wird mit derselben Bedingung.
  const v2 = upgradeListQuery(query);
  const own = headerCondition(v2, column);
  let found = false;
  const walk = (g: ListFilterGroup) => {
    for (const item of g.items) {
      if (item.type === 'group') walk(item);
      else if (item.type === 'condition' && item !== own && mentions(item, column)) found = true;
    }
  };
  walk(v2.where);
  return found;
}

/** Ohne leere Gruppen (sie schränken nicht ein); z. B. zum Vergleich mit dem gespeicherten Stand. */
export function withoutEmptyGroups(query: AnyListQuery): AnyListQuery {
  if (query.version === LIST_QUERY_VERSION) return query;
  const prune = (g: ListFilterGroup): ListFilterGroup => ({
    ...g,
    items: g.items.flatMap((item): ListFilterGroup['items'] => {
      if (item.type !== 'group') return [item];
      const inner = prune(item);
      return inner.items.length === 0 ? [] : [inner];
    }),
  });
  return { ...query, where: prune(query.where) };
}

/** Alle Bedingungen und Gruppen entfernen (Sortierung und Suche bleiben). */
export function clearConditions(query: AnyListQuery): ListQueryV2 {
  const v2 = upgradeListQuery(query);
  return { ...v2, where: { type: 'group', op: 'and', items: [] } };
}

/** Leere Beschreibung Version 2 (Wurzel „und“). */
export function emptyListQueryV2(): ListQueryV2 {
  return { version: LIST_QUERY_V2, where: { type: 'group', op: 'and', items: [] }, sort: null };
}

/**
 * Pfad eines ausgesetzten Filters im Baum der Version 2: Version 1 meldet `filters.N`, das entspricht
 * `where.items.N` der hochgestuften Beschreibung.
 */
export function suspendedTreePath(path: string): string {
  const m = /^filters\.(\d+)$/.exec(path);
  return m ? `where.items.${m[1]}` : path;
}
